<template>
    <EntityGrid
        title="Products & Services"
        :entities="products"
        :is-editing="isEditing"
        addButtonLabel="Add Product or Service"
        @add="openNewProduct()"
    >
        <EntityCard
            v-for="product in products"
            :key="product.identity"
            :entity="product"
            :entityType="EntityType.PRODUCT"
            border-color="var(--stekz-primary-color)"
            :to="{
                name: 'productPage',
                params: {
                    orgName: route.params.orgName,
                    stekName: route.params.stekName,
                    productId: product.identity,
                },
            }"
            :tags="[
                {
                    label: processesText(product),
                    severity: 'secondary',
                },
            ]"
        />
        <template #empty>
            <div class="empty-state">
                <i class="pi pi-box empty-icon"></i>
                <div>
                    <h3>No products or services set</h3>
                    <i>
                        <p v-if="isEditing">
                            Create your first product or service to get started.
                        </p>
                        <p v-else>
                            To add products and services, go into edit mode from
                            the top of the page.
                        </p>
                    </i>
                </div>
                <Button
                    v-if="isEditing"
                    label="Add Product or Service"
                    icon="pi pi-plus"
                    severity="primary"
                    @click="openNewProduct()"
                />
            </div>
        </template>
    </EntityGrid>
</template>

<script setup lang="ts">
import type { ProductInTree } from "@/stores/products-store";
import { useRoute } from "vue-router";
import { useNewProductDialog } from "@/composables/new-product-dialog";
import { computed } from "vue";
import EntityCard from "@/components/EntityInfo/EntityCard.vue";
import EntityGrid from "@/components/EntityInfo/EntityGrid.vue";
import { EntityType } from "@/stores/entity-store";

defineProps<{
    isEditing: boolean;
    products: ProductInTree[];
}>();

const route = useRoute();
const { open: openNewProduct } = useNewProductDialog();

const processesText = computed(() => {
    return (product: ProductInTree) => {
        const count = product.processes.length;
        if (count === 0) {
            return "No processes";
        } else if (count === 1) {
            return `${count} process`;
        } else {
            return `${count} processes`;
        }
    };
});
</script>

<style lang="scss" scoped>
.empty-state {
    display: flex;
    flex-direction: column;
    gap: 1em;
    align-items: center;
    text-align: center;

    > * {
        margin: 0;
    }

    .empty-icon {
        font-size: 3rem;
    }
}
</style>
